import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';
import QIcn from './queueIcons';

const TYPE_ICON = {
    translate: QIcn.translate,
    review: QIcn.review,
    approve: QIcn.approve,
    comment: QIcn.comment,
    glossary: QIcn.glossary,
};

const TYPE_CLS = {
    translate: '',
    review: '',
    approve: 'green',
    comment: 'indigo',
    glossary: 'yellow',
};

const GROUP_ORDER = ['Overdue', 'Due today', 'This week', 'Later'];

const FILTERS = [
    { id: 'All', label: 'All' },
    { id: 'translate', label: 'To translate' },
    { id: 'review', label: 'To review' },
    { id: 'approve', label: 'To approve' },
    { id: 'comment', label: 'Comments' },
    { id: 'glossary', label: 'Glossary' },
];

const Stats = ({ counts, byProject }) => (
    <div className="rs-q-stats">
        <div className="rs-q-stat">
            <div className="lbl"><span className="ic">{QIcn.bolt}</span>Tasks in queue</div>
            <div className="val">{counts.All || 0}</div>
            <div className="sub">across {byProject.length} project{byProject.length === 1 ? '' : 's'}</div>
        </div>
        <div className="rs-q-stat">
            <div className="lbl"><span className="ic indigo">{QIcn.clock}</span>Due today</div>
            <div className="val">{counts['Due today'] || 0}</div>
            <div className="sub">tasks scheduled today</div>
        </div>
        <div className="rs-q-stat">
            <div className="lbl"><span className="ic green">{QIcn.check}</span>To approve</div>
            <div className="val">{counts.approve || 0}<span className="unit">tasks</span></div>
            <div className="sub">final pass awaiting you</div>
        </div>
        <div className="rs-q-stat">
            <div className="lbl"><span className="ic yellow">{QIcn.star}</span>Overdue</div>
            <div className="val">{counts.Overdue || 0}</div>
            <div className="sub">{counts.Overdue ? 'needs attention now' : 'clean queue'}</div>
        </div>
    </div>
);

const TaskRow = ({ task }) => {
    const navigate = useNavigate();
    const go = () => {
        if (task.projectId) navigate(`/editor/${task.projectId}`);
    };
    return (
        <div className="rs-task" role="button" tabIndex={0} onClick={go}>
            <span className={`t-ic ${TYPE_CLS[task.type] || ''}`}>{TYPE_ICON[task.type] || QIcn.translate}</span>
            <div className="t-body">
                <div className="t-title">{task.title}</div>
                <div className="t-meta">
                    <b>{task.project}</b>
                    <span className="dot" />
                    <span>
                        {task.segs} segment{task.segs === 1 ? '' : 's'}
                        {task.words ? ` · ${task.words.toLocaleString()} words` : ''}
                    </span>
                </div>
            </div>
            <div className="t-chips">
                <span className="t-pair">
                    {task.pair[0]}<span className="arr">→</span>{task.pair[1]}
                </span>
            </div>
            <span className={`t-due ${task.grp === 'Overdue' ? 'warn' : task.grp === 'Due today' ? 'soon' : ''}`}>
                {task.due || '—'}
            </span>
            <button type="button" className="t-go" onClick={(e) => { e.stopPropagation(); go(); }}>
                {task.action} {QIcn.arrow}
            </button>
        </div>
    );
};

const TaskList = ({ tasks }) => {
    const grouped = GROUP_ORDER
        .map((g) => ({ name: g, items: tasks.filter((t) => t.grp === g) }))
        .filter((g) => g.items.length > 0);
    if (!grouped.length) {
        return (
            <div className="rs-card" style={{ padding: 48, textAlign: 'center', color: 'var(--ink-3)' }}>
                <div style={{ fontWeight: 600, fontSize: 16 }}>Your queue is clear</div>
                <div style={{ fontSize: 13, marginTop: 4 }}>Nothing assigned to you. Time for a coffee.</div>
            </div>
        );
    }
    return (
        <div className="rs-tasks">
            {grouped.map((g) => (
                <React.Fragment key={g.name}>
                    <div className={`rs-task-group ${g.name === 'Overdue' ? 'warn' : ''}`}>
                        <span>{g.name}</span>
                        <span className="ct">{g.items.length}</span>
                    </div>
                    {g.items.map((t) => <TaskRow key={t.id} task={t} />)}
                </React.Fragment>
            ))}
        </div>
    );
};

const SidePanel = ({ byProject, byLang, totalMinutes }) => (
    <div className="rs-q-side" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="rs-card rs-q-tip">
            <div className="tip-eye">Focus mode</div>
            <div className="tip-h">~{Math.max(20, totalMinutes)}m of work today</div>
            <p className="tip-p">Start with overdue items, then move through Due today. We'll hide other tabs once you start.</p>
            <button type="button" className="tip-btn">Start focus session</button>
        </div>

        <div className="rs-card">
            <h3>By project</h3>
            <div className="focus-bar">
                {byProject.length === 0 && <div className="focus-row"><span className="nm">No active projects</span></div>}
                {byProject.map((row) => (
                    <div className="focus-row" key={row.nm}>
                        <span className="nm">{row.nm}</span>
                        <span className="ct">{row.ct}</span>
                    </div>
                ))}
            </div>
        </div>

        <div className="rs-card">
            <h3>By language</h3>
            <div className="focus-bar">
                {byLang.length === 0 && <div className="focus-row"><span className="nm">—</span></div>}
                {byLang.map((row) => (
                    <div className="focus-row" key={row.nm}>
                        <span className="nm">{row.nm}</span>
                        <span className="ct">{row.ct}</span>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

const MyQueue = () => {
    const token = useSelector((s) => s.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [data, setData] = useState({ items: [], counts: {}, byProject: [], byLang: [] });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('All');
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) return;
        axios
            .get('/api/dashboard/my-queue', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                setData(r.data || { items: [], counts: {}, byProject: [], byLang: [] });
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load queue.');
                    setLoading(false);
                }
            });
    }, [token, dispatch, navigate]);

    const filtered = useMemo(() => {
        if (filter === 'All') return data.items;
        return data.items.filter((i) => i.type === filter);
    }, [filter, data.items]);

    const totalMinutes = useMemo(
        () => data.items.filter((i) => i.grp === 'Due today' || i.grp === 'Overdue').reduce((s, i) => s + Math.max(5, (i.segs || 1) * 3), 0),
        [data.items],
    );

    const groupCounts = useMemo(() => {
        const g = { Overdue: 0, 'Due today': 0, 'This week': 0, Later: 0 };
        data.items.forEach((i) => { g[i.grp] = (g[i.grp] || 0) + 1; });
        return g;
    }, [data.items]);

    const counts = { ...data.counts, ...groupCounts };

    if (loading) return <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading queue…</div>;
    if (error) return <div style={{ padding: 40, color: 'crimson' }}>{error}</div>;

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">My queue</h1>
                <span className="rs-page-title-count">{data.items.length}</span>
            </div>
            <p className="rs-page-sub">Tasks assigned to you across every project — sorted by what's due first.</p>

            <Stats counts={counts} byProject={data.byProject} />

            <div className="rs-views">
                {FILTERS.map((f) => (
                    <button
                        key={f.id}
                        type="button"
                        className={`rs-view-tab ${filter === f.id ? 'on' : ''}`}
                        onClick={() => setFilter(f.id)}
                    >
                        {f.label}
                        <span className="ct">{f.id === 'All' ? data.items.length : (data.counts[f.id] || 0)}</span>
                    </button>
                ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16, alignItems: 'start' }}>
                <TaskList tasks={filtered} />
                <SidePanel byProject={data.byProject} byLang={data.byLang} totalMinutes={totalMinutes} />
            </div>
        </>
    );
};

export default MyQueue;
